import { useMemo } from "react";
import { Canvas } from "@react-three/fiber";
import { OrbitControls, Text, Line } from "@react-three/drei";
import { rafBoyut, gozMerkez } from "@/lib/yol";

export const renk = (g) => (!g ? null : g.renkOzel || (g.bilinmeyen && !g.hacim ? "#94a3b8" : g.doluluk > 85 ? "#ef4444" : g.doluluk > 50 ? "#eab308" : "#22c55e"));
export const isiRenk = (t) => `hsl(${Math.round(220 - 220 * Math.min(1, Math.max(0, t)))}, 85%, 52%)`;
const kod = (r, s, g) => `${r.kod}-S${String(s).padStart(2, "0")}-G${g}`;

const Goz = ({ r, s, g, bilgi, secili, parla, onSec }) => {
  const m = gozMerkez(r, s, g);
  const ge = r.goz_en_cm / 100, gh = r.goz_yukseklik_cm / 100, gd = r.goz_derinlik_cm / 100;
  const dol = bilgi ? Math.max(0.18, Math.min(1, (bilgi.doluluk || 35) / 100)) : 0;
  const [sx, sz] = r.rot === 90 ? [gd * 0.85, ge * 0.85] : [ge * 0.85, gd * 0.85];
  const k = kod(r, s, g);
  return (
    <group>
      <mesh position={[m.x, m.y, m.z]} onClick={(e) => { e.stopPropagation(); onSec?.(k); }}>
        <boxGeometry args={[sx, gh * 0.9, sz]} />
        <meshStandardMaterial transparent opacity={secili ? 0.55 : parla ? 0.7 : 0.06} color={secili ? "#38bdf8" : parla ? "#22d3ee" : "#ffffff"} emissive={parla ? "#0891b2" : "#000000"} emissiveIntensity={parla ? 0.8 : 0} />
      </mesh>
      {bilgi && <mesh position={[m.x, m.y - (gh * 0.9) / 2 + (gh * 0.85 * dol) / 2, m.z]} onClick={(e) => { e.stopPropagation(); onSec?.(k); }}>
        <boxGeometry args={[sx * 0.92, gh * 0.85 * dol, sz * 0.92]} />
        <meshStandardMaterial color={renk(bilgi)} emissive={parla ? "#38bdf8" : "#000000"} emissiveIntensity={parla ? 0.9 : 0} />
      </mesh>}
    </group>
  );
};

const Raf = ({ r, gozler, secili, parlayan, onSec }) => {
  const { w, d } = rafBoyut(r);
  const H = r.kat * (r.goz_yukseklik_cm / 100) + 0.1;
  const dikme = [[r.x, r.z], [r.x + w, r.z], [r.x, r.z + d], [r.x + w, r.z + d]];
  return (
    <group>
      {dikme.map(([x, z], i) => <mesh key={i} position={[x, H / 2, z]}><boxGeometry args={[0.06, H, 0.06]} /><meshStandardMaterial color="#1d4ed8" /></mesh>)}
      {Array.from({ length: r.kat + 1 }, (_, g) => (
        <mesh key={g} position={[r.x + w / 2, 0.1 + g * (r.goz_yukseklik_cm / 100), r.z + d / 2]}><boxGeometry args={[w, 0.03, d]} /><meshStandardMaterial color="#9a3412" /></mesh>))}
      {Array.from({ length: r.sutun }, (_, s) => Array.from({ length: r.kat }, (_, g) => {
        const k = kod(r, s + 1, g + 1);
        return <Goz key={k} r={r} s={s + 1} g={g + 1} bilgi={gozler[k]} secili={secili === k} parla={parlayan?.has(k)} onSec={onSec} />;
      }))}
      <Text position={[r.x + w / 2, H + 0.45, r.z + d / 2]} fontSize={0.7} color="#f8fafc" anchorX="center" outlineWidth={0.04} outlineColor="#0f172a">{r.kod}</Text>
    </group>
  );
};

const Isaret = ({ p, renkK, yazi }) => (
  <group position={[p.x, 0, p.z]}>
    <mesh position={[0, 0.03, 0]} rotation={[-Math.PI / 2, 0, 0]}><circleGeometry args={[0.7, 24]} /><meshStandardMaterial color={renkK} /></mesh>
    <Text position={[0, 1.1, 0]} fontSize={0.8} color={renkK} anchorX="center" outlineWidth={0.05} outlineColor="#0f172a">{yazi}</Text>
  </group>
);

export const Depo3D = ({ plan, gozler = {}, secili, parlayan, rota, onSec, className = "h-[60vh]" }) => {
  const koridorlar = useMemo(() => {
    const m = {};
    plan.raflar.forEach((r) => { if (r.koridor) { const { w, d } = rafBoyut(r); (m[r.koridor] ||= []).push([r.x + w / 2, r.z + d / 2]); } });
    return Object.entries(m).map(([k, l]) => [k, l.reduce((a, p) => [a[0] + p[0] / l.length, a[1] + p[1] / l.length], [0, 0])]);
  }, [plan]);
  const merkez = [plan.en_m / 2, 0, plan.boy_m / 2];
  const uzak = Math.max(plan.en_m, plan.boy_m) * (typeof window !== "undefined" && window.innerWidth < 640 ? 1.9 : window.innerWidth < 1100 ? 1.3 : 1);
  return (
    <div className={`${className} w-full rounded-xl overflow-hidden bg-[#0b1220] touch-none`} data-testid="depo-3d">
      <Canvas dpr={[1, 2]} camera={{ position: [merkez[0] - uzak * 0.25, uzak * 0.45, merkez[2] + uzak * 0.5], fov: 50 }} onPointerMissed={() => onSec?.(null)}>
        <ambientLight intensity={0.7} />
        <directionalLight position={[20, 40, 20]} intensity={1.1} />
        <mesh rotation={[-Math.PI / 2, 0, 0]} position={merkez}><planeGeometry args={[plan.en_m, plan.boy_m]} /><meshStandardMaterial color="#64748b" /></mesh>
        <gridHelper args={[Math.max(plan.en_m, plan.boy_m), Math.round(Math.max(plan.en_m, plan.boy_m)), "#475569", "#334155"]} position={[merkez[0], 0.01, merkez[2]]} />
        {plan.raflar.map((r) => <Raf key={r.id || r.kod} r={r} gozler={gozler} secili={secili} parlayan={parlayan} onSec={onSec} />)}
        {koridorlar.map(([k, [x, z]]) => <Text key={k} position={[x, 0.05, z]} rotation={[-Math.PI / 2, 0, 0]} fontSize={1.2} color="#22c55e">{k}</Text>)}
        <Isaret p={plan.giris} renkK="#22c55e" yazi="GİRİŞ" />
        <Isaret p={plan.cikis} renkK="#ef4444" yazi="SEVKİYAT" />
        {rota?.noktalar?.length > 1 && <Line points={rota.noktalar.map(([x, z]) => [x, 0.12, z])} color="#4ade80" lineWidth={4} dashed dashSize={0.6} gapSize={0.3} />}
        <OrbitControls target={merkez} maxPolarAngle={Math.PI / 2.1} minDistance={3} maxDistance={uzak * 2.5} enableDamping />
      </Canvas>
    </div>
  );
};
